import { NavLink, Outlet } from 'react-router-dom';

/**
 * The Settings shell: a rail of groups, and one page per group.
 *
 * Each group is its own route rather than a card on one long page. The reason
 * is the save bar: `useEditorBar` gives a screen one Save button, and one
 * "Unsaved changes" spanning a password change and the terms of service would
 * be true about neither. Separate addresses also mean `/settings/legal` is a
 * link somebody can be sent, rather than "open Settings and scroll down".
 */

interface Group {
  to: string;
  label: string;
}

/*
 * The five the design lists, in its order.
 *
 * ⚠️ **Every one of them is a route that exists**, and `settings-routes.spec.tsx`
 * walks this list through the real `App` to prove it. There used to be a sixth —
 * Team — carried as grey text with "CLI for now" beside it, on the reasoning
 * that hiding an unbuilt section makes Settings look finished. The design
 * settled it the other way: its rail has these five and nothing else, and the
 * note slot it gives each item is empty in its own data. So the machinery for
 * "listed but not built" is gone with it. Adding a section here now means
 * adding the route too, or that spec fails.
 *
 * ⚠️ **Connections is third, between Security and Site copy** — not appended.
 * It sits with Security because both are about credentials, and away from Site
 * copy and Legal, which are both about published words.
 *
 * The paths are this admin's, not the design's mock keys: `site` was already a
 * published address before the design called that section `copy`, and renaming
 * it would break links people have. `connections` is spelled out for the same
 * reason the others are — the design's `keys` says nothing in an address bar.
 */
export const GROUPS: Group[] = [
  { to: 'profile', label: 'Profile' },
  { to: 'security', label: 'Security' },
  { to: 'connections', label: 'Connections' },
  { to: 'site', label: 'Site copy' },
  { to: 'legal', label: 'Legal' },
];

export default function SettingsPage() {
  return (
    <section className="flex flex-wrap items-start gap-3.5 px-[18px] pt-3.5 pb-6">
      {/*
        Sticky under the 50px header plus the section's own 14px of padding,
        so it holds its place in the row it started in. No page heading above
        it: the sticky header already says Settings, and the rail is what
        somebody comes back to as they scroll through a long list of sessions
        or a page of legal text.
      */}
      <nav
        className="sticky top-16 flex w-[150px] shrink-0 flex-col gap-0.5"
        aria-label="Settings sections"
      >
        {GROUPS.map((group) => (
          <NavLink
            key={group.to}
            to={group.to}
            /*
              Resting, hover and current all live in `.settings-item`. They were
              an inline `style` callback on `isActive`, which could say nothing
              about hover — inline styles have no pseudo-classes — so the rail
              was the one part of the admin with no response to the pointer at
              all, and the only response it did have was an underline the
              design does not draw.
            */
            className="settings-item focus-ring flex min-h-[30px] items-center gap-2 px-2.5 py-[7px] text-[12.5px]"
          >
            {group.label}
          </NavLink>
        ))}
      </nav>

      {/*
        Capped, where the design leaves it uncapped. Its own artboard is about
        1100px wide, so `1 1 420px` lands near 900 there; on a real 1600px
        screen the same rule stretches "Password · Change password" across
        1300px of empty panel, and a paragraph of explanation to a line nobody
        can track back to its start.
      */}
      <div className="min-w-0 max-w-[820px] flex-[1_1_420px]">
        <Outlet />
      </div>
    </section>
  );
}
